/**
 * Fix Strategy Engine
 * 
 * Maps each issue type to a concrete fix operation on the scene state.
 * Fixes mutate the source of truth (elements, globalSettings, etc.)
 * so the prompt rebuilds cleanly without the issue reappearing.
 */

import type { CanvasElement, GlobalSettings, AspectRatio } from '@/lib/engineTypes';
import type { TokenGroup, TokenConflict } from './tokenRegistry';
import { lockConstraint } from './tokenRegistry';
import type { PromptIssue } from '@/lib/promptInlineAnalyzer';

// ─── Issue IDs (deterministic, typed) ───

export type FixableIssueId =
  | 'camera_lens_conflict'
  | 'camera_perspective_conflict'
  | 'camera_shot_type_conflict'
  | 'camera_body_conflict'
  | 'camera_sensor_conflict'
  | 'aspect_ratio_conflict'
  | 'render_style_conflict'
  | 'color_temperature_conflict'
  | 'light_style_conflict'
  | 'light_preset_conflict'
  | 'film_stock_conflict'
  | 'lut_preset_conflict'
  | 'beauty_preset_conflict'
  | 'composition_conflict'
  | 'dof_mode_conflict'
  | 'duplicate_token'
  | 'style_duplication'
  | 'time_conflict'
  | 'weather_conflict'
  | 'mood_conflict'
  | 'environment_conflict'
  | 'vfx_overload'
  | 'prompt_text_issue';

export const TOKEN_GROUP_TO_ISSUE_ID: Record<TokenGroup, FixableIssueId> = {
  camera_lens: 'camera_lens_conflict',
  camera_perspective: 'camera_perspective_conflict',
  camera_shot_type: 'camera_shot_type_conflict',
  camera_body: 'camera_body_conflict',
  camera_sensor: 'camera_sensor_conflict',
  aspect_ratio: 'aspect_ratio_conflict',
  render_style: 'render_style_conflict',
  color_temperature: 'color_temperature_conflict',
  light_style: 'light_style_conflict',
  light_preset: 'light_preset_conflict',
  film_stock: 'film_stock_conflict',
  lut_preset: 'lut_preset_conflict',
  beauty_preset: 'beauty_preset_conflict',
  composition_primary: 'composition_conflict',
  dof_mode: 'dof_mode_conflict',
  time_of_day: 'time_conflict',
  weather: 'weather_conflict',
};

// ─── Scene Fix Context ───

export interface SceneFixContext {
  elements: CanvasElement[];
  globalSettings: GlobalSettings;
  aspectRatio: AspectRatio;
  stylePreset: string;
  sceneDescription: string;
}

export interface SceneFixResult {
  elements: CanvasElement[];
  globalSettings: GlobalSettings;
  aspectRatio: AspectRatio;
  stylePreset: string;
  sceneDescription: string;
  /** Which issue IDs were resolved */
  resolved: FixableIssueId[];
  /** Constraint locks applied */
  locksApplied: TokenGroup[];
}

// ─── Scene Description Sanitizer ───

/** Patterns that represent technical tokens we should strip from sceneDescription after a fix */
const TECHNICAL_TOKEN_PATTERNS: Array<{ group: TokenGroup; patterns: RegExp[] }> = [
  { group: 'camera_lens', patterns: [/\b\d{2,3}mm\s*(lens|prime)?\b/gi, /\btelephoto\b/gi, /\bwide[- ]?angle\b/gi] },
  { group: 'camera_perspective', patterns: [/\beye[- ]?level\s*(natural\s*)?perspective\b/gi, /\blow[- ]?angle\b/gi, /\bhigh[- ]?angle\b/gi, /\bbird'?s?[- ]?eye\b/gi, /\btop[- ]?down\b/gi, /\boverhead\b/gi, /\bdutch[- ]?angle\b/gi, /\bworm'?s?[- ]?eye\b/gi] },
  { group: 'camera_shot_type', patterns: [/\b(extreme\s+)?close[- ]?up\b/gi, /\bmedium\s+shot\b/gi, /\bwide\s+shot\b/gi, /\bfull\s+shot\b/gi, /\bcowboy\s+shot\b/gi, /\bestablishing\s+shot\b/gi] },
  { group: 'camera_body', patterns: [/\b(dslr|mirrorless|film\s+camera|hasselblad|leica|arri|red\s+camera)\b/gi] },
  { group: 'camera_sensor', patterns: [/\b(full[- ]?frame|aps[- ]?c|medium\s+format|super\s+35)\b/gi] },
  { group: 'aspect_ratio', patterns: [/\b\d+:\d+\b/g] },
  { group: 'render_style', patterns: [/\b(photorealistic|hyperrealistic|cinematic|anime|cartoon|3d\s+render|oil\s+painting|watercolor)\b/gi] },
  { group: 'color_temperature', patterns: [/\b\d{4,5}K\b/gi, /\b(warm|cool|cold|daylight)\s+color\s+temperature\b/gi] },
  { group: 'dof_mode', patterns: [/\b(shallow|deep|moderate)\s+(depth\s+of\s+field|dof|bokeh)\b/gi] },
];

/**
 * Remove technical tokens belonging to a specific group from scene description.
 * Keeps the non-technical parts of the user's text intact.
 */
const stripTokenGroupFromDescription = (text: string, group: TokenGroup): string => {
  const entry = TECHNICAL_TOKEN_PATTERNS.find((p) => p.group === group);
  if (!entry) return text;
  
  let result = text;
  for (const pattern of entry.patterns) {
    result = result.replace(pattern, '');
  }
  return result.replace(/,\s*,/g, ', ').replace(/\s{2,}/g, ' ').replace(/,\s*$/, '').trim();
};

/**
 * Strip ALL technical tokens from scene description that are already
 * represented by canvas elements. This prevents the raw user text from
 * re-introducing conflicts that the fix resolved at the element level.
 */
export const sanitizeSceneDescription = (text: string, groupsToStrip: TokenGroup[]): string => {
  let result = text;
  for (const group of groupsToStrip) {
    result = stripTokenGroupFromDescription(result, group);
  }
  return result.replace(/,\s*,/g, ', ').replace(/\s{2,}/g, ' ').replace(/,\s*$/, '').trim();
};

// ─── Fix Strategies ───

type FixStrategy = (ctx: SceneFixContext, conflict: TokenConflict) => SceneFixContext;

const applyTokenConflictFix: FixStrategy = (ctx, conflict) => {
  const { winner, losers } = conflict;
  let { elements, globalSettings, stylePreset, sceneDescription } = ctx;

  for (const loser of losers) {
    if (loser.source === 'element' && loser.elementId && loser.propertyKey) {
      elements = elements.map((el) => {
        if (el.id !== loser.elementId) return el;
        return {
          ...el,
          properties: { ...el.properties, [loser.propertyKey!]: winner.value },
        };
      });
    }

    if (loser.source === 'global' && loser.propertyKey) {
      if (conflict.group === 'composition_primary') {
        const compositionMap: Record<string, keyof GlobalSettings> = {
          rule_of_thirds: 'ruleOfThirds',
          centered: 'centeredCinematic',
          golden_ratio: 'goldenRatio',
          symmetry: 'symmetryMode',
        };
        const settingKey = compositionMap[loser.value];
        if (settingKey) {
          globalSettings = { ...globalSettings, [settingKey]: false } as GlobalSettings;
        }
      } else {
        globalSettings = { ...globalSettings, [loser.propertyKey]: winner.value } as GlobalSettings;
      }
    }

    if (loser.source === 'style' && loser.propertyKey === 'stylePreset') {
      stylePreset = 'none';
    }
  }

  // Also strip conflicting tokens from scene description to prevent re-injection
  sceneDescription = stripTokenGroupFromDescription(sceneDescription, conflict.group);

  // Apply constraint lock
  lockConstraint(conflict.group, winner.value, `Fixed ${conflict.group} conflict`);

  return { ...ctx, elements, globalSettings, stylePreset, sceneDescription };
};

// ─── Prompt Text Issue Fix (for issues detected in the text itself) ───

const fixPromptTextIssue = (ctx: SceneFixContext, issue: PromptIssue): SceneFixContext => {
  const { sceneDescription } = ctx;
  if (!sceneDescription) return ctx;

  // If the issue is in the scene description text, apply the fix there
  const lower = sceneDescription.toLowerCase();
  const issueText = issue.fix?.replacement !== undefined
    ? sceneDescription.slice(issue.start, issue.end)
    : '';

  if (!issueText) return ctx;

  const start = lower.indexOf(issueText.toLowerCase());
  if (start === -1) return ctx;

  const replacement = issue.fix?.replacement ?? '';
  const fixed = sceneDescription.slice(0, start) + replacement + sceneDescription.slice(start + issueText.length);
  return {
    ...ctx,
    sceneDescription: fixed.replace(/\s{2,}/g, ' ').replace(/,\s*,/g, ', ').trim(),
  };
};

// ─── Public API ───

/**
 * Fix a single token conflict by mutating the scene context.
 */
export const fixTokenConflict = (
  ctx: SceneFixContext,
  conflict: TokenConflict,
): SceneFixResult => {
  const issueId = TOKEN_GROUP_TO_ISSUE_ID[conflict.group] || 'duplicate_token';
  const fixed = applyTokenConflictFix(ctx, conflict);
  return {
    ...fixed,
    resolved: [issueId],
    locksApplied: [conflict.group],
  };
};

/**
 * Fix a specific prompt-text issue by adjusting scene description.
 */
export const fixPromptIssue = (
  ctx: SceneFixContext,
  issue: PromptIssue,
): SceneFixResult => {
  const fixed = fixPromptTextIssue(ctx, issue);
  return {
    ...fixed,
    resolved: ['prompt_text_issue'],
    locksApplied: [],
  };
};

/**
 * AUTO_REPAIR: Fix ALL detected token conflicts in severity order.
 */
export const autoRepairAll = (
  ctx: SceneFixContext,
  conflicts: TokenConflict[],
  textIssues: PromptIssue[],
): SceneFixResult => {
  let current = { ...ctx };
  const resolved: FixableIssueId[] = [];
  const locksApplied: TokenGroup[] = [];

  // 1. Fix token conflicts (highest priority first)
  const sorted = [...conflicts].sort((a, b) => {
    const severityOrder: Record<string, number> = {
      camera_lens: 10, camera_shot_type: 9, render_style: 8,
      color_temperature: 7, composition_primary: 6, light_style: 5,
    };
    return (severityOrder[b.group] || 0) - (severityOrder[a.group] || 0);
  });

  for (const conflict of sorted) {
    const result = fixTokenConflict(current, conflict);
    current = result;
    resolved.push(...result.resolved);
    locksApplied.push(...result.locksApplied);
  }

  // 2. Fix remaining text issues
  const fixableTextIssues = textIssues.filter((i) => i.type !== 'valid' && i.fix);
  for (const issue of fixableTextIssues) {
    const result = fixPromptIssue(current, issue);
    current = result;
    resolved.push(...result.resolved);
  }

  return {
    ...current,
    resolved: [...new Set(resolved)],
    locksApplied: [...new Set(locksApplied)],
  };
};
